import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import api from "@/lib/api";
import { todayJKT } from "@/lib/tz";
import RupiahInput from "@/components/RupiahInput";

export default function CollectionFollowupDialog({ open, row, meta, onClose, onSaved }) {
  const [form, setForm] = useState({
    followup_date: todayJKT(),
    media: "telp",
    contact_name: "",
    contact_phone: "",
    result: "no_response",
    promise_date: "",
    promise_amount: null,
    next_followup_date: "",
    notes: "",
    first_reminder: "1d",
    second_reminder: "none",
  });
  const [saving, setSaving] = useState(false);
  if (!open || !row) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/collection/followups", {
        project_id: row.project_id,
        entry_id: row.entry_id,
        ...form,
      });
      toast.success("Follow-up tersimpan (audit-safe, tidak menimpa histori lama)");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="followup-dialog">
      <div className="bg-white w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="bg-[#0B1D3A] text-white px-4 py-3 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold">Catat Follow-up</h3>
            <div className="text-xs text-white/70">{row.client} · {row.project_name}</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Tanggal *</label>
              <input required type="date" data-testid="input-followup-date" value={form.followup_date}
                onChange={(e) => setForm({ ...form, followup_date: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Media *</label>
              <select data-testid="select-followup-media" value={form.media}
                onChange={(e) => setForm({ ...form, media: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2 text-sm">
                {meta.comm_media.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Nama Kontak</label>
              <input type="text" data-testid="input-followup-contact" value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Telp/HP</label>
              <input type="text" value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Hasil *</label>
              <select data-testid="select-followup-result" value={form.result}
                onChange={(e) => setForm({ ...form, result: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2 text-sm">
                {meta.result_kinds.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            {form.result === "promise" && (
              <>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Tanggal Janji Bayar</label>
                  <input type="date" data-testid="input-promise-date" value={form.promise_date}
                    onChange={(e) => setForm({ ...form, promise_date: e.target.value })}
                    className="w-full border border-slate-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Nilai Janji</label>
                  <RupiahInput data-testid="input-promise-amount" value={form.promise_amount || 0}
                    onChange={(v) => setForm({ ...form, promise_amount: v })} />
                </div>
              </>
            )}
            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Follow-up Berikutnya</label>
              <input type="date" data-testid="input-next-followup" value={form.next_followup_date}
                onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2 text-sm" />
            </div>
            {form.next_followup_date && (
              <>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Reminder Pertama</label>
                  <select value={form.first_reminder} onChange={(e) => setForm({ ...form, first_reminder: e.target.value })}
                    className="w-full border border-slate-300 px-3 py-2 text-sm">
                    <option value="1d">1 hari sebelum</option>
                    <option value="3d">3 hari sebelum</option>
                    <option value="1w">1 minggu sebelum</option>
                    <option value="none">Tanpa reminder</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Reminder Kedua</label>
                  <select value={form.second_reminder} onChange={(e) => setForm({ ...form, second_reminder: e.target.value })}
                    className="w-full border border-slate-300 px-3 py-2 text-sm">
                    <option value="none">Tanpa</option>
                    <option value="1d">1 hari sebelum</option>
                    <option value="3d">3 hari sebelum</option>
                    <option value="1w">1 minggu sebelum</option>
                  </select>
                </div>
              </>
            )}
            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Catatan</label>
              <textarea rows={3} data-testid="input-followup-notes" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Rangkuman percakapan, komitmen, kendala…"
                className="w-full border border-slate-300 px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="text-[10px] text-slate-500 italic border-t border-slate-200 pt-2">
            Follow-up bersifat <span className="font-semibold">append-only</span>. Catatan lama tidak akan tertimpa.
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider border border-slate-300">Batal</button>
            <button type="submit" data-testid="btn-save-followup" disabled={saving}
              className="px-4 py-2 text-xs uppercase tracking-wider bg-[#FF5A1F] hover:bg-[#e04d15] text-white disabled:opacity-50">
              {saving ? "Menyimpan..." : "Simpan Follow-up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
